import React, { useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useTheme } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { alpha } from '@mui/material/styles';

let diagramCounter = 0;

const messages = defineMessages({
  renderError: {
    id: 'phcToolkit.mermaidDiagram.renderError',
    defaultMessage: 'Could not render the diagram.',
  },
});

export interface MermaidDiagramProps {
  /** Mermaid source (e.g. "flowchart TD ..."). */
  chart: string;
  className?: string;
  /**
   * Message shown when mermaid fails to render the supplied source. Defaults to
   * a generic "Could not render the diagram." string.
   */
  errorMessage?: React.ReactNode;
}

/**
 * Renders a mermaid diagram from its DSL source as inline SVG. Mermaid is loaded
 * lazily so it stays out of the main bundle, and rendering failures fall back to
 * a small message rather than breaking the surrounding view. Colors are driven
 * from the active chroma theme so the diagram matches the app (including dark
 * mode) instead of mermaid's defaults.
 *
 * Shared across the AI Rules flow diagram, Zori chat messages, and Zori report
 * documents so mermaid renders consistently wherever agent-authored markdown can
 * contain a ```mermaid fence.
 */
export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({
  chart,
  className,
  errorMessage,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasError, setHasError] = useState(false);

  // Map the chroma/MUI palette onto mermaid's `base` theme variables. Re-computed
  // when the theme (e.g. light <-> dark) changes so the effect below re-renders.
  const themeVariables = useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    const nodeFill = alpha(theme.palette.primary.main, isDark ? 0.22 : 0.08);
    const surface = theme.palette.background.default;
    return {
      background: theme.palette.background.paper,
      primaryColor: nodeFill,
      primaryTextColor: theme.palette.text.primary,
      primaryBorderColor: theme.palette.primary.main,
      secondaryColor: surface,
      secondaryTextColor: theme.palette.text.primary,
      secondaryBorderColor: theme.palette.divider,
      tertiaryColor: surface,
      tertiaryTextColor: theme.palette.text.primary,
      tertiaryBorderColor: theme.palette.divider,
      mainBkg: nodeFill,
      nodeBorder: theme.palette.primary.main,
      nodeTextColor: theme.palette.text.primary,
      lineColor: theme.palette.text.secondary,
      textColor: theme.palette.text.primary,
      clusterBkg: surface,
      clusterBorder: theme.palette.divider,
      edgeLabelBackground: theme.palette.background.paper,
      fontFamily: theme.typography.fontFamily ?? 'inherit',
      fontSize: '14px',
    };
  }, [theme]);

  useEffect(() => {
    let cancelled = false;

    if (!chart || !chart.trim()) {
      setHasError(false);
      return;
    }

    const renderDiagram = async () => {
      try {
        const { default: mermaid } = await import('mermaid');
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'base',
          themeVariables,
          flowchart: {
            useMaxWidth: false,
            wrappingWidth: 180,
          },
        });
        diagramCounter += 1;
        const { svg } = await mermaid.render(
          `mermaid-diagram-${diagramCounter}`,
          chart,
        );
        if (!cancelled && containerRef.current) {
          containerRef.current.innerHTML = svg;
          setHasError(false);
        }
      } catch {
        if (!cancelled) {
          if (containerRef.current) {
            containerRef.current.innerHTML = '';
          }
          setHasError(true);
        }
      }
    };

    void renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [chart, themeVariables]);

  if (hasError) {
    return (
      <Text size="caption">
        {errorMessage ?? formatMessage(messages.renderError)}
      </Text>
    );
  }

  return <div ref={containerRef} className={className} />;
};

export default MermaidDiagram;
